import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useEffect, useState } from "react";

export default function ModalSpecificHours({
  isModalOpen,
  setIsModalOpen,
  onSaveSpecificHours,
  disabledDays,
  errorMessage,
  errorMessageOverLap,
  editingSpecificHour,
  onEditSpecificHour,
}) {
  const [selectedDate, setSelectedDate] = useState(editingSpecificHour ? editingSpecificHour.date : "");
  const [timeBlocks, setTimeBlocks] = useState(editingSpecificHour ? editingSpecificHour.timeBlocks : []);

  useEffect(() => {
    if (isModalOpen) {
      if (editingSpecificHour) {
        // Si se está editando, inicializar con los valores de editingSpecificHour
        setSelectedDate(editingSpecificHour.date);
        setTimeBlocks(editingSpecificHour.timeBlocks);
      } else {
        // Si no se está editando, reiniciar los estados
        setSelectedDate("");
        setTimeBlocks([]);
      }
    }
  }, [isModalOpen, editingSpecificHour]);
 
  

  // Función para convertir una hora en formato "HH:MM" a minutos desde la medianoche
  const timeToMinutes = (time) => {
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
  };

  // Función para verificar si dos bloques de tiempo se superponen
  const isOverlapping = (block1, block2) => {
    const start1 = timeToMinutes(block1.start);
    const end1 = timeToMinutes(block1.end);
    const start2 = timeToMinutes(block2.start);
    const end2 = timeToMinutes(block2.end);

    return start1 < end2 && end1 > start2;
  };

  const handleAddTimeBlock = () => {
    if (disabledDays.includes(selectedDate)) {
      errorMessage();
      return;
    }

    // Obtener la hora de finalización del último bloque (si existe)
    const lastBlock = timeBlocks[timeBlocks.length - 1];
    const defaultStart = lastBlock ? lastBlock.end : "09:00"; // Si no hay bloques, empezar a las 09:00
    const defaultEnd = "17:00"; // Hora de finalización predeterminada

    const newBlock = { start: defaultStart, end: defaultEnd };

    // Verificar si el nuevo bloque se superpone con los bloques existentes
    const isOverlap = timeBlocks.some((block) => isOverlapping(block, newBlock));

    if (isOverlap) {
      errorMessageOverLap()
      return;
    }

    setTimeBlocks([...timeBlocks, newBlock]);
  };

  const handleTimeChange = (index, field, value) => {
    const updatedTimeBlocks = [...timeBlocks];
    const updatedBlock = { ...updatedTimeBlocks[index], [field]: value };

    // Verificar si el bloque modificado se superpone con otros bloques
    const isOverlap = timeBlocks.some((block, i) => {
      if (i === index) return false; // Ignorar el bloque actual
      return isOverlapping(block, updatedBlock);
    });

    if (isOverlap) {
      errorMessageOverLap()
      return;
    }

    updatedTimeBlocks[index] = updatedBlock;
    setTimeBlocks(updatedTimeBlocks);
  };

  const handleRemoveTimeBlock = (index) => {
    const updatedTimeBlocks = timeBlocks.filter((_, i) => i !== index);
    setTimeBlocks(updatedTimeBlocks);
  };

  const handleSave = () => {
    if (disabledDays.includes(selectedDate)) {
      errorMessage();
      return;
    }

    // Verificar si hay superposiciones antes de guardar
    for (let i = 0; i < timeBlocks.length; i++) {
      for (let j = i + 1; j < timeBlocks.length; j++) {
        if (isOverlapping(timeBlocks[i], timeBlocks[j])) {
          errorMessageOverLap();
          return;
        }
      }
    }

    if (editingSpecificHour) {
      // Si se está editando, llama a onEditSpecificHour
      onEditSpecificHour(selectedDate, timeBlocks);
    } else {
      // Si no se está editando, llama a onSaveSpecificHours
      onSaveSpecificHours(selectedDate, timeBlocks);
    }

    setIsModalOpen(false);
  };

  return (
    <AnimatePresence>
      {isModalOpen && (
        <motion.div
          key="modal-background"
          onClick={() => setIsModalOpen(false)}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-20"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          <motion.div
            key="modal-content"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="bg-background rounded-lg shadow-lg sm:p-6 p-2 font-poppins w-[500px] mx-4 border border-gray-100"
          >
            <h2 className="text-xl font-semibold mb-4 text-text">Edit Specific Hours</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">Select Date</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                min={new Date().toISOString().split("T")[0]} // Fecha mínima: hoy
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">Time Blocks</label>
              {timeBlocks.map((block, index) => (
                <div key={index} className="flex items-center gap-2 mb-2">
                  <input
                    type="time"
                    value={block.start}
                    onChange={(e) => handleTimeChange(index, "start", e.target.value)}
                    className="px-2 py-1 border rounded"
                  />
                  <span>-</span>
                  <input
                    type="time"
                    value={block.end}
                    onChange={(e) => handleTimeChange(index, "end", e.target.value)}
                    className="px-2 py-1 border rounded"
                  />
                  <button
                    onClick={() => handleRemoveTimeBlock(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X/>
                  </button>
                </div>
              ))}
              <button
                onClick={handleAddTimeBlock}
                className="text-blue-500 hover:text-blue-700"
              >
                + Add Time Block
              </button>
            </div>
            <div className="mt-6 flex justify-end gap-4">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                Save Changes
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}